import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ROUTES } from "#root/routes/constants";
import { showToast } from "#root/components/GlobalToaster";
import { usePersonById, useUpdatePerson } from "#root/queries/persons";
import VictimForm, { VictimFormData } from "../VictimForm";
import LoadingWrapper from "#root/components/LoadingWrapper";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import { FILE_KINDS, FILE_REFERENCE_TYPES, useFileUpload } from "#root/hooks/useFileUpload";
import { SCARS_LABEL, TATTOO_LABEL } from "#root/utils/persons";
import { useFileDelete } from "#root/hooks/useFileDelete";
import { UploaderFile } from "#root/components/ImageUploader";
import { FileUploadPayload } from "#root/hooks/useFileUpload";
import { urlToFile } from "#root/services/utils";

const EditVictim: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const personId = searchParams.get("personId");
  const { data: victim, isLoading: isVictimLoading } = usePersonById({ id: personId! });
  const [isLoading, setIsLoading] = useState(false);
  const updateVictim = useUpdatePerson();
  const { uploadImages } = useFileUpload();
  const { deleteImages } = useFileDelete();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [victimForm, setVictimForm] = useState<VictimFormData | null>(null);
  const [deletedFileIds, setDeletedFileIds] = useState<string[]>([]);

  useEffect(() => {
    const processMediaFiles = async () => {
      if (!victim?.item) return;

      setIsLoading(true);
      const item = victim.item;
      const personalImages: UploaderFile[] = [];
      const licenseImages: UploaderFile[] = [];
      const scarImages: UploaderFile[] = [];
      const tattooImages: UploaderFile[] = [];
      const invalidFileIds: string[] = [];

      if (item.mediaFiles?.length) {
        await Promise.all(
          item.mediaFiles
            .filter((media): media is { url: string; kind: string; id?: string } => 
              Boolean(media.url) && Boolean(media.kind)
            )
            .map(async media => {
              try {
                const file = await urlToFile(media.url, media.id);
                switch (media.kind) {
                  case FILE_KINDS.PERSONAL_IMAGE:
                    personalImages.push(file);
                    break;
                  case FILE_KINDS.LICENSE_ID:
                    licenseImages.push(file);
                    break;
                  case FILE_KINDS.SCAR:
                    scarImages.push(file);
                    break;
                  case FILE_KINDS.TATTOO:
                    tattooImages.push(file);
                    break;
                  default:
                    console.warn("Unknown media kind:", media.kind);
                }
              } catch (error) {
                console.error(`Error converting file for ${media.kind}:`, error);
                if (media.id) {
                  invalidFileIds.push(media.id);
                }
              }
            })
        );

        // Delete invalid files
        if (invalidFileIds.length > 0) {
          try {
            await deleteImages(invalidFileIds);
            showToast(`Removed ${invalidFileIds.length} invalid media files`, "info");
          } catch (error) {
            console.error("Error deleting invalid files:", error);
          }
        }
      }

      setVictimForm({
        ...item,
        unitId: item.unitId ?? "",
        department: item.department ?? "",
        incidentId: item.incidentId ?? "",
        foundLocation: item.foundLocation ?? "",
        firstName: item.firstName ?? "",
        lastName: item.lastName ?? "",
        status: item.status ?? "",
        additionalInfo: item.additionalInfo ?? "",
        locationId: item.locationId ?? "",
        restrictions: item.restrictions ?? true,
        gender: item.gender ?? undefined,
        age: item.age ?? undefined,
        weight: item.weight ?? undefined,
        height: item.height ?? undefined,
        eyes: item.eyes ?? undefined,
        hair: item.hair ?? undefined,
        features: item.features ?? [],
        personalImage: personalImages[0],
        licenseImage: licenseImages[0],
        scarImages,
        tattooImages,
      });

      setIsLoading(false);
    };

    processMediaFiles();
  }, [victim, deleteImages]);

  const handleSubmit = async (formData: Omit<VictimFormData, "id">) => {
    if (!personId) return;
    try {
      setIsSubmitting(true);

      if (deletedFileIds.length > 0) {
        await deleteImages(deletedFileIds);
      }

      const filesToUpload: FileUploadPayload[] = [];

      if (formData.personalImage && !formData.personalImage.id) {
        filesToUpload.push({
          file: formData.personalImage,
          referenceId: personId,
          kind: FILE_KINDS.PERSONAL_IMAGE,
          referenceType: FILE_REFERENCE_TYPES.PERSON
        });
      }
      if (formData.licenseImage && !formData.licenseImage.id) {
        filesToUpload.push({
          file: formData.licenseImage,
          referenceId: personId,
          kind: FILE_KINDS.LICENSE_ID,
          referenceType: FILE_REFERENCE_TYPES.PERSON
        });
      }
      if (formData.features) {
        if (
          formData.features.includes(SCARS_LABEL.value) &&
          formData.scarImages.length > 0
        ) {
          formData.scarImages
            .filter(file => !file.id)
            .forEach(file => {
              filesToUpload.push({
                file,
                referenceId: personId,
                kind: FILE_KINDS.SCAR,
                referenceType: FILE_REFERENCE_TYPES.PERSON
              });
            });
        }
        if (
          formData.features.includes(TATTOO_LABEL.value) &&
          formData.tattooImages.length > 0
        ) {
          formData.tattooImages
            .filter(file => !file.id)
            .forEach(file => {
              filesToUpload.push({
                file,
                referenceId: personId,
                kind: FILE_KINDS.TATTOO,
                referenceType: FILE_REFERENCE_TYPES.PERSON
              });
            });
        }
      }

      if (filesToUpload.length > 0) {
        await uploadImages(filesToUpload);
      }

      await updateVictim.mutateAsync({
        person: {
          ...formData,
          id: personId,
        },
      });

      showToast("Victim updated successfully", "success");
      navigate(ROUTES.VTRACKER.ALL_VICTIMS);
    } catch (error) {
      showToast("Something went wrong. Please try again or contact the administrator", "error");
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="edit-contact">
      <div className="emc-page-header">
        <div className="emc-page-header__top-row">
          <div className="emc-page-header__breadcrumbs">
            <Breadcrumbs />
          </div>
        </div>
        <div className="emc-page-header__title">
          <h2>
            Edit Victim <div className="divider"></div>
          </h2>
        </div>
      </div>

      <LoadingWrapper
        isLoading={isVictimLoading || isLoading}
        isEmpty={!victimForm}
        emptyMessage="Victim not found"
        emptyIcon={<i className="fi fi-sr-file-user" />}
      >
        {victimForm && (
          <VictimForm
            initialData={victimForm}
            onSubmit={handleSubmit}
            submitButtonText="Save"
            isSubmitting={isSubmitting}
            onFileDelete={(fileId: string) => {
              setDeletedFileIds(prev => [...prev, fileId]);
            }}
          />
        )}
      </LoadingWrapper>
    </div>
  );
};

export default EditVictim;
